import { createSlice } from "@reduxjs/toolkit";
import { current } from "immer";

const initialState = {
  overviewShown: false,
  sampleWorkInstructionsShown: false,
  assignmentData: {
    changed: false,
  },
  rubricData: {
    changed: false,
    issue_categories: [
      { id: 1,
        name: "Spelling errors",
        description: "Do not include simple substitutions that likely result from OCR errors, such as | for I, 1 for l, 0 for O, etc.",
        selected: false,
        userCreated: false,
      },
      // { id: 2,
      //   name: "Punctuation errors",
      //   description: "",
      //   selected: false,
      //   userCreated: false,
      // },
      // { id: 3,
      //   name: "Run-on sentences",
      //   description: "Identify sentences that are too long and should be broken up into multiple shorter sentences to improve clarity.",
      //   selected: false,
      //   userCreated: false,
      // },
      // { id: 4,
      //   name: "Quality of hook",
      //   description: "Identify issues with the opening sentence or introductory paragraph. In your suggestion, include 2 specific examples of how the student can strengthen their hook. Keep phrasing of your examples simple and clear. Use simple words and sentences, appropriate for the student's grade level.",
      //   selected: false,
      //   userCreated: false,
      // },
      // { id: 5,
      //   name: "Integration of quotes",
      //   description: "Identify lack of, or inappropriate or inaccurate integration of quotes. Each quote should be introduced, be woven in grammatically, and be analysed or assessed.",
      //   selected: false,
      //   userCreated: false,
      // },
      // { id: 6,
      //   name: "Evidence & analysis",
      //   description: "Identify instances where evidence or analysis is weak or missing. In your suggestion, include specific examples on how student can improve their work in this area.",
      //   selected: false, 
      //   userCreated: false,
      // },
      // { id: 7,
      //   name: "Logical inconsistencies",
      //   description: "Identify flaws within the reasoning that lead to contradictions, unsupported assumptions, or conclusions that do not follow logically from the arguments presented. This could be contradictory statements, non-sequiturs, unsupported assumptions, circular reasoning, faulty causal relationships.",
      //   selected: false,
      //   userCreated: false,
      // },
      // { id: 8,
      //   name: "Improper Passive Voice use",
      //   description: "Identify overuse of the passive voice. The passive voice is a grammatical construction in which the typical object of an action becomes the subject of a sentence. This shifts the focus from the agent (the doer) to the recipient of the action. In passive voice sentences, the agent either follows the verb phrase with 'by' or is omitted entirely.",
      //   selected: false,
      //   userCreated: false,
      // },
    ],
  },
  studentWorkData: { },
  sampleWorkData: {
    changed: false,
    num_samples: 1,
  },
  sessionName: "",
  sessionDescription: "",
  assignmentId: 0,
  rubricId: 0,
  studentWorkId: 0,
  sampleWorkId:0,
  gradingCalibrationId:0,
  gradingCalibrationData: {},
  teacherRubricGrades: {
    grades: {},
    regrade_count: 0,
  },
  generalFb: {
    type: "general",
    header: "",
    generalCategories: ["Strengths", "Areas for growth"],
    grading_id: 0,
    feedbacks: [
      {
        id: 1,
        generalCategory: "Strengths",
        fbText: "",
        edited: false,
        selected: true,
      },
      {
        id: 2,
        generalCategory: "Areas for growth",
        fbText: "",
        edited: false,
        selected: true,
      },
    ],
  },
  rubricCriteriaFb: {
    type: "rubric",
    header: "Rubric-based grades and feedback",
    grading_id: 0,
    feedbacks: [
      {
        id: 1,
        criterion: "Development of ideas",
        fbText: "",
        edited: false,
        selected: true,
      },
      {
        id: 2,
        criterion: "Organization",
        fbText: "",
        edited: false,
        selected: true,
      },
    ],
  },
  categoryFb: {
    type: "category",
    header: "Category-based feedback",
    grading_id: 0,
  },
  overallFb: {
    type: "overall",
    header: "",
    grading_id: 0,
    id: "overall",
    fbText: "",
    edited: false,
    selected: true,
  },
};

function updateFeedbacks(state, action, feedbackType) {
  const { fb_id, fbText } = action.payload;
  const feedbackPath = state[feedbackType].feedbacks;
  const updatedFeedbacks = feedbackPath.map(fb => fb.id === fb_id ? {
    ...fb,
    edited: true,
    ...(fbText ? { fbText } : {}),
  } : fb);
  state[feedbackType].feedbacks = updatedFeedbacks;
}

/**
 * A utility function to update a specific part of the Redux state and track changes.
 *
 * This function merges incoming updates into the specified state key and determines 
 * whether the "changed" flag should be set to `true`. It handles deep comparisons 
 * for arrays to ensure accurate change detection.
 *
 * @param {object} state - The current state draft (provided by Immer in Redux Toolkit).
 * @param {object} updates - The updates to apply to the specified state key.
 * @param {string} key - The key in the state to be updated (e.g., "assignmentData", "rubricData").
 *
 * Behavior:
 * - Merges `updates` into the specified key of the state.
 * - Sets the `changed` flag to `true` unless:
 *   - The `changed` key explicitly exists in `updates` and is `false`.
 *   - No meaningful changes are detected in the updated fields.
 * - Uses deep comparison for arrays to ensure accurate detection of changes.
 *
 * Usage:
 * Call this function inside a reducer to handle common "update and track changes" logic
 * across multiple parts of the state, reducing code duplication.
 */
const updateStateWithChanges = (state, updates, key) => {
  const areArraysEqual = (arr1, arr2) => {
    const deepEqual = (obj1, obj2) => {
      if (obj1 === obj2) return true;

      if (
        obj1 === null ||
        obj2 === null ||
        typeof obj1 !== "object" ||
        typeof obj2 !== "object"
      ) {
        return false;
      }

      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);

      if (keys1.length !== keys2.length) return false;

      return keys1.every((key) => deepEqual(obj1[key], obj2[key]));
    };

    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;

    if (arr1.length !== arr2.length) return false;

    return arr1.every((val, index) => deepEqual(val, arr2[index]));
  };

  const hasChanges = Object.entries(updates).some(([updateKey, updateValue]) => {
    // Immer drafts can still wrap values, so handle Proxies if they exist
    const existingValue = state[key]?.[updateKey] ?? undefined;

    if (Array.isArray(existingValue) && Array.isArray(updateValue)) {
      const arrayChanged = !areArraysEqual(
        JSON.parse(JSON.stringify(existingValue)),
        JSON.parse(JSON.stringify(updateValue))
      );
      return arrayChanged;
    }

    const bothFalsy = !existingValue && !updateValue;
    const valueChanged = !bothFalsy && existingValue !== updateValue;

    return valueChanged;
  });

  // Apply updates to the state
  state[key] = { ...state[key], ...updates };

  // Set `changed` to true unless the `changed` key explicitly exists and is false
  if (hasChanges || updates.changed === true) {
    state[key].changed = updates.changed ?? true;
  }
};


const updateState = (state, updates, key) => {  
  state[key] = { ...state[key], ...updates };
};


const gradingFormSlice = createSlice({
  name: "gradingForm",
  initialState: initialState,
  reducers: {

    // Specific reducers calling the general function
    editGeneralFb: (state, action) => {
      updateFeedbacks(state, action, 'generalFb');
    },

    editRubricCriteriaFb: (state, action) => {
      updateFeedbacks(state, action, 'rubricCriteriaFb');
    },

    editCategoryFb: (state, action) => {
      updateFeedbacks(state, action, 'categoryFb');
    },

    editOverallFb: (state, action) => {
      const { fbText } = action.payload;
      state.overallFb = {
        ...state.overallFb,
        edited: true,
        ...(fbText ? { fbText } : {}),
      };
    },

    setAssignmentData: (state, action) => {
      updateStateWithChanges(state, action.payload, "assignmentData");
    },

    setRubricData: (state, action) => {
      updateStateWithChanges(state, action.payload, "rubricData");
    },

    setSampleWorkData: (state, action) => {
      updateStateWithChanges(state, action.payload, "sampleWorkData");
    },




    //update state without change variable
    setAssignmentDataWithoutChange: (state, action) => {
      updateState(state, action.payload, "assignmentData");
    },

    setRubricDataWithoutChange: (state, action) => {
      updateState(state, action.payload, "rubricData");
    },

    setSampleWorkDataWithoutChange: (state, action) => {
      updateState(state, action.payload, "sampleWorkData");
    },


    initAssignmentData: (state, action) => {
      const updates = action.payload;
      state.assignmentData = { ...state.assignmentData, ...updates };
    },


    
    initRubricData: (state, action) => {
      const updates = action.payload;
      state.rubricData = { ...state.rubricData, ...updates };
    },
    initSampleWorkData: (state, action) => {
      const updates = action.payload;
      state.sampleWorkData = { ...state.sampleWorkData, ...updates };
    },
    setStudentWorkData: (state, action) => {
      const updates = action.payload;
      state.studentWorkData = { ...state.studentWorkData, ...updates };
    },
    replaceSampleWorkData: (state, action) => {
      state.sampleWorkData = action.payload;
      state.sampleWorkData.changed = true;
    },

    updateTeacherRubricGrade: (state, action) => {
      const {criterion, grade} = action.payload;
      
      // Ensure grades is an object if it's not already
      if (!state.teacherRubricGrades.grades) {
        state.teacherRubricGrades.grades = {};
      }
      // Update or add the grade for the given criterion
      state.teacherRubricGrades.grades[criterion] = grade;
    },
    updateTeacherRubricGradeData: (state, action) => {
      const updates = action.payload;
      state.teacherRubricGrades = { ...state.teacherRubricGrades, ...updates };
    },

    setOverviewShown: (state, action) => {
      const overviewShown = action.payload;
      state.overviewShown = overviewShown;
    },

    setSampleWorkInstructionsShown: (state, action) => {
      const sampleWorkInstructionsShown = action.payload;
      state.sampleWorkInstructionsShown = sampleWorkInstructionsShown;
    },

    setSessionName: (state, action) => {
      const sessionName = action.payload;
      state.sessionName = sessionName;
    },
    setSessionDescription: (state, action) => {
      const sessionDescription = action.payload;
      state.sessionDescription = sessionDescription;
    },
    setAssignmentId: (state, action) => {
      const assignmentId = action.payload;
      state.assignmentId = assignmentId;
    },
    setRubricId: (state, action) => {
      const rubricId = action.payload;
      state.rubricId = rubricId;
    },
    setStudentWorkId: (state, action) => {
      const studentWorkId = action.payload;
      state.studentWorkId = studentWorkId;
    },
    setSampleWorkId: (state, action) => {
      const sampleWorkId = action.payload;
      state.sampleWorkId = sampleWorkId;
    },
    setGradingCalibrationId: (state, action) => {
      const gradingCalibrationId = action.payload;
      state.gradingCalibrationId = gradingCalibrationId;
    },

    setNumSamples: (state, action)=>{
      const num_samples = action.payload;
      state.sampleWorkData.num_samples = num_samples;
    },

    setIssueCategories: (state, action) => {
      const issue_categories = action.payload;
      state.issue_categories = issue_categories;
    },
    setGradingCalibrationData: (state, action) => {
      const gradingCalibrationData = action.payload;
      state.gradingCalibrationData = gradingCalibrationData;
    },
    setGeneralFb: (state, action) => {
      const generalFb = action.payload;
      state.generalFb = generalFb;
    },
    setRubricCriteriaFb: (state, action) => {
      const rubricCriteriaFb = action.payload;
      state.rubricCriteriaFb = rubricCriteriaFb;
    },
    setCategoryFb: (state, action) => {
      const categoryFb = action.payload;
      state.categoryFb = categoryFb;
    },
    setOverallFb: (state, action) => {
      const overallFb = action.payload;
      state.overallFb = overallFb;
    },

    setExampleValues: (state, action) => {
      state = initialState;
    },

    setDefaultValues: (state, action) => {
      // First reset the state to the initialState
      Object.assign(state, initialState);
      
      // Then update immutably
      state.assignmentData = {
        subject: localStorage.getItem("subject") || "",
        grade: localStorage.getItem("grade") || "",
      };
    },

    resetStudentWorkData: (state, action) => {
      state.studentWorkData = {};
      state.teacherRubricGrades = initialState.teacherRubricGrades;
      state.generalFb = initialState.generalFb;
      state.rubricCriteriaFb = initialState.rubricCriteriaFb;
      state.categoryFb = initialState.categoryFb;
      state.overallFb = initialState.overallFb;
    },
    resetSampleWorkData: (state, action) => {
      state.sampleWorkData = {};
    },
    resetTeacherRubricGrades: (state, action) => {
      state.teacherRubricGrades = initialState.teacherRubricGrades;
    },

  },
});

export const {
  setOverviewShown,
  setSampleWorkInstructionsShown,
  setAssignmentData,
  setRubricData,
  setStudentWorkData,
  initAssignmentData,
  initRubricData,
  initSampleWorkData,
  setSampleWorkData,
  replaceSampleWorkData,
  setSampleWorkId,
  setGradingCalibrationId,
  setSessionName,
  setSessionDescription,
  setAssignmentId,
  setRubricId,
  setStudentWorkId,
  setIssueCategories,
  setGeneralFb,
  setRubricCriteriaFb,
  setCategoryFb,
  setOverallFb,
  setExampleValues,
  setDefaultValues,
  editGeneralFb,
  editRubricCriteriaFb,
  editOverallFb,
  editCategoryFb,
  resetStudentWorkData,
  resetSampleWorkData,
  resetTeacherRubricGrades,
  setNumSamples,
  setGradingCalibrationData,
  updateTeacherRubricGrade,
  updateTeacherRubricGradeData,
  setAssignmentDataWithoutChange,
  setRubricDataWithoutChange,
  setSampleWorkDataWithoutChange

} = gradingFormSlice.actions;

export default gradingFormSlice.reducer;

export const selectOverviewShown = (state) => state.gradingForm.overviewShown;
export const selectSampleWorkInstructionsShown = (state) => state.gradingForm.sampleWorkInstructionsShown;
export const selectAssignmentData = (state) => state.gradingForm.assignmentData;
export const selectRubricData = (state) => state.gradingForm.rubricData;
export const selectStudentWorkData = (state) => state.gradingForm.studentWorkData;
export const selectSampleWorkData = (state) => state.gradingForm.sampleWorkData;
export const selectSessionName = (state) => state.gradingForm.sessionName;
export const selectSessionDescription = (state) => state.gradingForm.sessionDescription;
export const selectAssignmentId = (state) => state.gradingForm.assignmentId;
export const selectRubricId = (state) => state.gradingForm.rubricId;
export const selectSampleWorkId = (state) => state.gradingForm.sampleWorkId;
export const selectGradingCalibrationId = (state) => state.gradingForm.gradingCalibrationId;
export const selectStudentWorkId = (state) => state.gradingForm.studentWorkId;
export const selectIssueCategories = (state) => state.gradingForm.issue_categories;
export const selectGeneralFb = (state) => state.gradingForm.generalFb;
export const selectRubricCriteriaFb = (state) => state.gradingForm.rubricCriteriaFb;
export const selectCategoryFb = (state) => state.gradingForm.categoryFb;
export const selectOverallFb = (state) => state.gradingForm.overallFb;
export const selectNumSamples = (state) => state.gradingForm.sampleWorkData.num_samples;
export const selectGradingCalibrationData = (state) => state.gradingForm.gradingCalibrationData;
export const selectTeacherRubricGrades = (state) => state.gradingForm.teacherRubricGrades;
export const selectRegradeCount = (state) => state.gradingForm.teacherRubricGrades.regrade_count;



